import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartResponsiveContainer } from "../../../components/ChartResponsiveContainer";
import { COLORS } from "../../../constants";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import {
  SLICE_PALETTE,
  aggregateByLoja,
  aggregateByRotulo,
} from "../utils/relatorioSaboresChartData";

type TooltipPayload = {
  payload?: {
    name?: string;
    value?: number;
    size?: number;
    fullName?: string;
  };
};

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipPayload[];
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const label = p?.fullName ?? p?.name ?? "";
  const val = p?.value ?? p?.size;
  if (val == null) return null;
  return (
    <div className="max-w-xs rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{label}</p>
      <p className="mt-1 text-neutral-600">
        Quantidade: <span className="font-medium text-neutral-900">{val}</span>
      </p>
    </div>
  );
};

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  chartHeightPx?: number;
}> = ({ title, subtitle, children, className = "", chartHeightPx }) => (
  <div
    className={`col-span-1 w-full min-w-0 rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-5 ${className}`}
  >
    <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
    {subtitle && <p className="mt-1 text-xs text-neutral-500">{subtitle}</p>}
    <div
      className={
        chartHeightPx == null
          ? "relative mt-4 h-[22rem] min-h-[352px] w-full min-w-0 sm:h-[26rem] sm:min-h-[400px]"
          : "relative mt-4 w-full min-w-0"
      }
      style={
        chartHeightPx != null
          ? { height: chartHeightPx, minHeight: chartHeightPx }
          : undefined
      }
    >
      {children}
    </div>
  </div>
);

const PIE_PARTICIPACAO_TOP_SLICES = 10;

const PizzasPorSaborCharts: React.FC = () => {
  const { linhas, isLoading, appliedFilters } = useRelatorioSabores();

  const byRotuloPie = useMemo(
    () => aggregateByRotulo(linhas, PIE_PARTICIPACAO_TOP_SLICES),
    [linhas]
  );
  const byLoja = useMemo(() => aggregateByLoja(linhas), [linhas]);
  const horizontalBars = useMemo(() => aggregateByRotulo(linhas), [linhas]);

  const rankingChartHeightPx = useMemo(
    () => Math.max(400, 48 + horizontalBars.length * 28),
    [horizontalBars.length]
  );

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
        Carregando gráficos…
      </div>
    );
  }

  if (appliedFilters == null) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
        Clique em{" "}
        <span className="font-medium text-neutral-700">Aplicar filtros</span>{" "}
        para carregar o painel. A consulta só roda após você confirmar o período
        e os filtros.
      </div>
    );
  }

  if (linhas.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/80 py-16 text-center text-sm text-neutral-500">
        Nenhum dado no período para os filtros escolhidos.
      </div>
    );
  }

  return (
    <section aria-label="Gráficos">
      <h2 className="mb-4 text-lg font-semibold text-neutral-900">
        Painel visual
      </h2>
      <p className="mb-6 w-full text-sm text-neutral-600">
        Leia o mesmo resultado em pizza e barras — ideal para apresentar em
        reunião ou TV.
      </p>
      <div className="grid w-full grid-cols-1 gap-8">
        <ChartCard
          title="Participação por descrição"
          subtitle={`As ${PIE_PARTICIPACAO_TOP_SLICES} descrições com maior volume no período; quantidades menores são somadas em «Outros». O ranking completo está no gráfico de barras abaixo.`}
        >
          <ChartResponsiveContainer initialHeightPx={352}>
            <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <Pie
                data={byRotuloPie}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="48%"
                innerRadius="38%"
                outerRadius="72%"
                paddingAngle={1.5}
                labelLine={false}
                label={({ percent }) =>
                  (percent ?? 0) >= 0.05
                    ? `${((percent ?? 0) * 100).toFixed(0)}%`
                    : ""
                }
              >
                {byRotuloPie.map((_, i) => (
                  <Cell
                    key={`c-${i}`}
                    fill={SLICE_PALETTE[i % SLICE_PALETTE.length]}
                    stroke={COLORS.WHITE}
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                iconSize={10}
                wrapperStyle={{
                  fontSize: "11px",
                  lineHeight: 1.35,
                  paddingTop: 12,
                  maxHeight: "7.5rem",
                  overflowY: "auto",
                  width: "100%",
                }}
                formatter={(value) => (
                  <span className="text-neutral-700">{value}</span>
                )}
              />
            </PieChart>
          </ChartResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Volume por loja"
          subtitle="Barras verticais — soma de todas as linhas da loja."
        >
          <ChartResponsiveContainer initialHeightPx={352}>
            <BarChart
              data={byLoja}
              margin={{ top: 8, right: 8, left: 0, bottom: 40 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={COLORS.NEUTRAL_200}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-28}
                textAnchor="end"
                height={56}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Quantidade">
                {byLoja.map((_, i) => (
                  <Cell
                    key={`b-${i}`}
                    fill={SLICE_PALETTE[i % SLICE_PALETTE.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Ranking de descrições"
          subtitle="Barras horizontais — todas as descrições com volume no período."
          chartHeightPx={rankingChartHeightPx}
        >
          <ChartResponsiveContainer initialHeightPx={rankingChartHeightPx}>
            <BarChart
              layout="vertical"
              data={horizontalBars}
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={COLORS.NEUTRAL_200}
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={118}
                tick={{ fontSize: 10 }}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="value"
                radius={[0, 6, 6, 0]}
                name="Quantidade"
                fill={COLORS.PRIMARY}
              />
            </BarChart>
          </ChartResponsiveContainer>
        </ChartCard>
      </div>
    </section>
  );
};

export default PizzasPorSaborCharts;
